import React, { useEffect } from 'react';
import { Manga, TierGrade, TIER_ORDER } from '../types/manga';
import { isUpdatedToday } from '../utils/helpers';
import { MangaCard } from './MangaCard';
import { TierHeading } from './TierBoard';
import { X } from 'lucide-react';

interface UpdatedTodayModalProps {
  isOpen: boolean;
  onClose: () => void;
  mangaList: Manga[];
  onQuickPlus: (id: string) => void;
  onQuickMinus: (id: string) => void;
  onEdit: (manga: Manga) => void;
  onOpenSourceDetails: (manga: Manga) => void;
}

export const UpdatedTodayModal: React.FC<UpdatedTodayModalProps> = ({
  isOpen,
  onClose,
  mangaList,
  onQuickPlus,
  onQuickMinus,
  onEdit,
  onOpenSourceDetails,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const tiers = TIER_ORDER;
  const updatedManga = mangaList.filter(isUpdatedToday);

  return (
    <div className="modal-overlay safe-area-modal">
      <div onClick={(e) => e.stopPropagation()} className="modal-panel max-w-5xl">
        {/* Fixed Header */}
        <div className="modal-header">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
            <h3 className="modal-title">อัปเดตวันนี้</h3>
            <span className="text-xs text-zinc-500 tabular-nums">{updatedManga.length} เรื่อง</span>
          </div>
          <button onClick={onClose} title="ปิด" className="icon-btn -mr-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Grouped by Tier like the main board */}
        <div className="modal-body space-y-8" style={{ WebkitOverflowScrolling: 'touch' }}>
          {updatedManga.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-sm sm:text-base text-zinc-300">วันนี้ยังไม่มีตอนใหม่ที่ค้างอ่าน</p>
              <p className="text-xs sm:text-sm text-zinc-500 mt-1.5">
                ระบบจะเช็คตอนใหม่จากทุกเว็บให้อัตโนมัติแบบเรียลไทม์
              </p>
            </div>
          ) : (
            tiers.map((tierGrade) => {
              const tierManga = updatedManga.filter((m) => m.grade === tierGrade);
              if (tierManga.length === 0) return null;

              return (
                <section key={tierGrade}>
                  <TierHeading grade={tierGrade} count={tierManga.length} />

                  {/* Exactly 3 columns on mobile, up to 6 on PC */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-x-2.5 gap-y-5 sm:gap-x-4 sm:gap-y-6 items-stretch">
                    {tierManga.map((manga) => (
                      <MangaCard
                        key={manga.id}
                        manga={manga}
                        isSortMode={false}
                        onQuickPlus={onQuickPlus}
                        onQuickMinus={onQuickMinus}
                        onEdit={onEdit}
                        onOpenSourceDetails={onOpenSourceDetails}
                      />
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
